import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Typography, Box, CircularProgress, Chip, Avatar, IconButton,
  Alert, Tooltip, TextField, Pagination, Stack, InputAdornment, Card, CardContent, MenuItem
} from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import PeopleIcon from '@mui/icons-material/People';
import RefreshIcon from '@mui/icons-material/Refresh';
import SearchIcon from '@mui/icons-material/Search';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import {
  UserService,
  formatUserDate,
  getUserDisplayName,
  getUserInitials,
  getSubscriptionLabel,
} from '../services/UserService';
import { CACHE_KEYS } from '../services/cache/cacheKeys';
import { CACHE_TTL } from '../services/cache/dataCache';
import { useCachedQuery } from '../hooks/useCachedQuery';
import PageHeader from '../components/ui/PageHeader';
import EmptyState from '../components/ui/EmptyState';
import DataTableCard from '../components/ui/DataTableCard';

const PAGE_SIZE = 20;

const statusColor = (status?: string): 'success' | 'warning' | 'default' | 'error' => {
  if (status === 'active') return 'success';
  if (status === 'pending') return 'warning';
  if (status === 'blocked' || status === 'banned') return 'error';
  return 'default';
};

const stringToColor = (value: string): string => {
  let hash = 0;
  for (let i = 0; i < value.length; i++) {
    hash = value.charCodeAt(i) + ((hash << 5) - hash);
  }
  let color = '#';
  for (let i = 0; i < 3; i++) {
    const channel = (hash >> (i * 8)) & 0xff;
    color += `00${channel.toString(16)}`.slice(-2);
  }
  return color;
};

const Users: React.FC = () => {
  const navigate = useNavigate();
  const fetchUsers = useCallback(() => UserService.getUsers(), []);

  const { data: users, loading, refetch } = useCachedQuery(
    CACHE_KEYS.users,
    fetchUsers,
    { ttlMs: CACHE_TTL.users }
  );

  const [search, setSearch] = useState('');
  const [proFilter, setProFilter] = useState<'all' | 'pro' | 'free'>('all');
  const [emailFilter, setEmailFilter] = useState<'all' | 'verified' | 'unverified'>('all');
  const [page, setPage] = useState(1);
  const [gotoPage, setGotoPage] = useState('');
  const [gotoError, setGotoError] = useState('');

  const userList = useMemo(() => users ?? [], [users]);

  const filteredUsers = useMemo(() => {
    const term = search.trim().toLowerCase();
    return userList.filter((user) => {
      if (proFilter === 'pro' && !user.isPro) return false;
      if (proFilter === 'free' && user.isPro) return false;
      if (emailFilter === 'verified' && !user.emailVerified) return false;
      if (emailFilter === 'unverified' && user.emailVerified) return false;

      if (!term) return true;

      const haystack = [
        user.username,
        user.email,
        user.id,
        user.status,
        user.subscription?.subscriptionType,
        user.subscription?.subscriptionStatus,
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return haystack.includes(term);
    });
  }, [userList, search, proFilter, emailFilter]);

  const totalPages = Math.max(1, Math.ceil(filteredUsers.length / PAGE_SIZE));

  const paginatedUsers = useMemo(
    () => filteredUsers.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE),
    [filteredUsers, page]
  );

  useEffect(() => {
    setPage(1);
  }, [search, proFilter, emailFilter]);

  useEffect(() => {
    if (page > totalPages) setPage(totalPages);
  }, [page, totalPages]);

  const handlePageChange = (_: React.ChangeEvent<unknown>, value: number) => {
    setPage(value);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleGotoPage = () => {
    const num = Number(gotoPage);
    if (!num || num < 1 || num > totalPages) {
      setGotoError(`1 - ${totalPages} arasında bir sayfa girin`);
      return;
    }
    setPage(num);
    setGotoError('');
    setGotoPage('');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  if (loading && !users) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100%' }}>
      <PageHeader
        title="Kullanıcılar"
        subtitle={
          userList.length > 0
            ? `${userList.length} kayıtlı kullanıcı${search || proFilter !== 'all' || emailFilter !== 'all' ? ` · ${filteredUsers.length} sonuç` : ''} · Sayfa ${page}/${totalPages}`
            : 'Kullanıcı arayın ve detay sayfasından bilgileri düzenleyin'
        }
        icon={<PeopleIcon />}
        actions={
          <Tooltip title="Listeyi yenile">
            <span>
              <IconButton onClick={() => refetch(true)} disabled={loading} size="small">
                <RefreshIcon />
              </IconButton>
            </span>
          </Tooltip>
        }
      />

      <Alert
        severity="info"
        icon={<InfoOutlinedIcon />}
        sx={{ mb: 3, borderRadius: 2 }}
      >
        <Typography variant="body2" fontWeight={600} gutterBottom>
          E-posta doğrulama iki yerde tutuluyor
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Kullanıcı e-postasını <strong>Firebase Authentication</strong> üzerinden doğrular (maildeki link/kod).
          Ancak mobil uygulama doğrulama sonrası <strong>Firestore</strong> içindeki{' '}
          <code>emailVerified</code> ve <code>status</code> alanlarını güncellemiyorsa burada
          &quot;Doğrulanmadı&quot; ve &quot;pending&quot; görünür. Kalıcı çözüm: iOS uygulamasında
          doğrulama sonrası Firestore güncellenmeli. Geçici çözüm:{' '}
          <code>scripts/sync-auth-email-verified.mjs</code> scripti ile Auth verisini Firestore&apos;a senkron edin.
        </Typography>
      </Alert>

      <Card sx={{ mb: 3 }}>
        <CardContent sx={{ p: 2.5, '&:last-child': { pb: 2.5 } }}>
          <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
            <TextField
              size="small"
              placeholder="Kullanıcı adı, e-posta veya ID ile ara..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              sx={{ flex: 1, minWidth: 240 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" color="action" />
                  </InputAdornment>
                ),
              }}
            />
            <TextField
              select
              size="small"
              label="Abonelik"
              value={proFilter}
              onChange={(e) => setProFilter(e.target.value as 'all' | 'pro' | 'free')}
              sx={{ minWidth: 140 }}
            >
              <MenuItem value="all">Tümü</MenuItem>
              <MenuItem value="pro">Pro</MenuItem>
              <MenuItem value="free">Free</MenuItem>
            </TextField>
            <TextField
              select
              size="small"
              label="E-posta Doğrulama"
              value={emailFilter}
              onChange={(e) => setEmailFilter(e.target.value as 'all' | 'verified' | 'unverified')}
              sx={{ minWidth: 160 }}
            >
              <MenuItem value="all">Tümü</MenuItem>
              <MenuItem value="verified">Doğrulanmış</MenuItem>
              <MenuItem value="unverified">Doğrulanmamış</MenuItem>
            </TextField>
          </Stack>
        </CardContent>
      </Card>

      {filteredUsers.length === 0 ? (
        <EmptyState
          icon={<PeopleIcon sx={{ fontSize: 36 }} />}
          title={search || proFilter !== 'all' || emailFilter !== 'all' ? 'Sonuç bulunamadı' : 'Kullanıcı bulunamadı'}
          description={
            search || proFilter !== 'all' || emailFilter !== 'all'
              ? 'Filtreleri değiştirerek tekrar deneyin.'
              : 'Firebase users koleksiyonunda henüz kullanıcı kaydı yok.'
          }
        />
      ) : (
        <DataTableCard subtitle={`Sayfa ${page} — ${paginatedUsers.length} kayıt · Satıra tıklayarak detaya gidin`}>
          <TableContainer>
            <Table sx={{ minWidth: 1080 }}>
              <TableHead>
                <TableRow>
                  <TableCell>Kullanıcı</TableCell>
                  <TableCell>E-posta</TableCell>
                  <TableCell>
                    <Tooltip title="Firestore users.status — mobil uygulama kayıt sırasında yazıyor">
                      <span>Hesap Durumu</span>
                    </Tooltip>
                  </TableCell>
                  <TableCell>
                    <Tooltip title="Firestore users.emailVerified — Auth ile senkron olmayabilir">
                      <span>Firestore E-posta</span>
                    </Tooltip>
                  </TableCell>
                  <TableCell>Abonelik</TableCell>
                  <TableCell>Abone Olma</TableCell>
                  <TableCell>Kayıt Tarihi</TableCell>
                  <TableCell align="right" />
                </TableRow>
              </TableHead>
              <TableBody>
                {paginatedUsers.map((user) => (
                  <TableRow
                    key={user.id}
                    hover
                    sx={{ cursor: 'pointer' }}
                    onClick={() => navigate(`/users/${user.id}`)}
                  >
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Avatar
                          sx={{
                            width: 32,
                            height: 32,
                            fontSize: '0.75rem',
                            fontWeight: 600,
                            bgcolor: stringToColor(user.email || user.id),
                          }}
                        >
                          {getUserInitials(user)}
                        </Avatar>
                        <Typography variant="body2" fontWeight={500}>
                          {getUserDisplayName(user)}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{user.email || '—'}</Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={user.status || '—'}
                        size="small"
                        color={statusColor(user.status)}
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={user.emailVerified ? 'Doğrulandı' : 'Doğrulanmadı'}
                        size="small"
                        color={user.emailVerified ? 'success' : 'warning'}
                        variant={user.emailVerified ? 'filled' : 'outlined'}
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={getSubscriptionLabel(user)}
                        size="small"
                        color={user.isPro ? 'primary' : 'default'}
                        variant={user.isPro ? 'filled' : 'outlined'}
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        {formatUserDate(user.subscription?.subscriptionDate || user.proActivatedAt)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        {formatUserDate(user.createdAt)}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <ChevronRightIcon fontSize="small" color="action" />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </DataTableCard>
      )}

      {filteredUsers.length > PAGE_SIZE && (
        <Box display="flex" flexWrap="wrap" justifyContent="center" alignItems="center" mt={4} gap={2}>
          <Pagination
            count={totalPages}
            page={page}
            onChange={handlePageChange}
            color="primary"
            shape="rounded"
            showFirstButton
            showLastButton
          />
          <Stack direction="row" alignItems="flex-start" spacing={0.5}>
            <TextField
              size="small"
              label="Sayfaya Git"
              value={gotoPage}
              onChange={(e) => setGotoPage(e.target.value.replace(/[^0-9]/g, ''))}
              onKeyDown={(e) => e.key === 'Enter' && handleGotoPage()}
              error={!!gotoError}
              helperText={gotoError}
              sx={{ width: 120 }}
            />
            <IconButton color="primary" onClick={handleGotoPage} aria-label="Git" sx={{ mt: 0.5 }}>
              <ArrowForwardIcon />
            </IconButton>
          </Stack>
        </Box>
      )}
    </Box>
  );
};

export default Users;
